import Phaser from "phaser";
import { findPath, getResourcePileLabel } from "@/core";
import type { ResourceType, TileState, WorldState } from "@/core";
import type { GameApp } from "../app/GameApp";
import { FloatingRewardManager } from "./floatingRewards";
import { ENEMY_TEMPLATES, scaleEnemyToLevel } from "../core/roscha";

export const TILE_SIZE = 128;
const DEFAULT_MAP_SIZE = 100;

const TERRAIN_COLORS: Record<string, number> = {
  grass: 0x4ade80,
  forest: 0x166534,
  water: 0x0ea5e9,
  mountain: 0x6b7280,
  road: 0xd4a574,
  sand: 0xfcd34d,
};

const FOG_COLORS = {
  undiscovered: 0x0f172a,
  discovered: 0x1e293b,
};


const RESOURCE_COLORS: Record<ResourceType, number> = {
  gold: 0xfbbf24,
  mana: 0x8b5cf6,
};


const DECO_VARIANTS: Record<string, number> = {
  tree: 4,
  bush: 4,
  rock: 3,
  flower: 3,
};


function tileHash(x: number, y: number, i: number): number {
  let h = (x * 374761 + y * 668265 + i * 982451) | 0;
  h = ((h >> 16) ^ h) * 0x45d9f3b | 0;
  h = ((h >> 16) ^ h) * 0x45d9f3b | 0;
  return (h ^ (h >> 16)) >>> 0;
}


const DECO_CONFIG: Record<string, {
  count: [number, number];        
  scale: [number, number];        
  offsetRange: number;            
  sameVariant?: boolean;          
}> = {
  tree:   { count: [3, 5], scale: [0.65, 1.05], offsetRange: 36 },
  bush:   { count: [1, 2], scale: [0.45, 0.7], offsetRange: 35 },
  rock:   { count: [1, 2], scale: [0.4, 0.65], offsetRange: 30 },
  flower: { count: [2, 4], scale: [0.45, 0.65], offsetRange: 35, sameVariant: true },
};

export class MapScene extends Phaser.Scene {
  private app!: GameApp;
  private mapContainer!: Phaser.GameObjects.Container;
  private mapGraphics!: Phaser.GameObjects.Graphics;
  private decoContainer!: Phaser.GameObjects.Container;
  private pathGraphics!: Phaser.GameObjects.Graphics;
  private heroSprite!: Phaser.GameObjects.Arc;
  private unsub?: () => void;
  private unsubFloating?: () => void;
  private floaters?: FloatingRewardManager;
  private pendingFloaters: import("../app/GameApp").FloatingRewardEvent[] = [];
  private tooltip?: Phaser.GameObjects.Container;
  private resizeHandler?: () => void;
  private isAnimatingMove = false;
  private movementTween?: Phaser.Tweens.Tween | Phaser.Tweens.TweenChain;
  private safetyTimeoutId?: number;
  private lastHeroPos?: { x: number; y: number };
  private pendingTarget?: { x: number; y: number };
  private plannedPath?: Array<{ x: number; y: number }>;
  private originalMoveTarget?: { x: number; y: number };

  private terrainContainer!: Phaser.GameObjects.Container;
  private terrainPool: Phaser.GameObjects.Image[] = [];
  private terrainPoolIdx = 0;

  private decoPool: Phaser.GameObjects.Image[] = [];
  private decoPoolIdx = 0;

  private isDragging = false;
  private pointerdownInside = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private camStartX = 0;
  private camStartY = 0;
  private readonly DRAG_THRESHOLD = 8;

  constructor() {
    super({ key: "MapScene" });
  }

  preload() {
    for (let i = 1; i <= 3; i++) this.load.image(`terrain-grass-${i}`, `/map/terrain/grass_0${i}.png`);
    this.load.image('terrain-sand-1',  '/map/terrain/sand_01.png');
    this.load.image('terrain-water-1', '/map/terrain/water_01.png');
    this.load.image('terrain-road-1',  '/map/terrain/road_01.png');

    const PAIRS = [['grass', 'sand'], ['grass', 'road'], ['sand', 'water']] as const;
    const POSITIONS = [1, 2, 3, 4, 6, 7, 8, 9];
    for (const [a, b] of PAIRS) {
      for (const p of POSITIONS) {
        const n = String(p).padStart(2, '0');
        this.load.image(`terrain-${a}-${b}-${n}`, `/map/terrain/${a}-${b}_${n}.png`);
      }
    }

    for (let i = 1; i <= 4; i++) this.load.image(`tree_0${i}`, `/map/deco/tree_0${i}.png`);
    for (let i = 1; i <= 4; i++) this.load.image(`bush_0${i}`, `/map/deco/bush_0${i}.png`);
    for (let i = 1; i <= 3; i++) this.load.image(`rock_0${i}`, `/map/deco/rock_0${i}.png`);
    for (let i = 1; i <= 3; i++) this.load.image(`flower_0${i}`, `/map/deco/flower_0${i}.png`);
    if (!this.textures.exists("resIconGold")) this.load.image("resIconGold", "/map/icons/icon_gold.png");
    if (!this.textures.exists("resIconMana")) this.load.image("resIconMana", "/map/icons/icon_mana.png");
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;

    this.terrainPool = [];
    this.terrainPoolIdx = 0;
    this.decoPool = [];
    this.decoPoolIdx = 0;
    this.lastCamX = -1;
    this.lastCamY = -1;
    this.lastHeroPos = undefined;
    this.isAnimatingMove = false;
    this.pendingTarget = undefined;
    this.plannedPath = undefined;
    this.movementTween = undefined;

    this.mapContainer = this.add.container(0, 0);

    this.terrainContainer = this.add.container(0, 0);
    this.mapContainer.add(this.terrainContainer);

    this.mapGraphics = this.add.graphics();
    this.mapContainer.add(this.mapGraphics);

    this.decoContainer = this.add.container(0, 0);
    this.mapContainer.add(this.decoContainer);

    this.pathGraphics = this.add.graphics();
    this.mapContainer.add(this.pathGraphics);

    this.heroSprite = this.add.circle(0, 0, TILE_SIZE / 2 - 8, 0x22c55e);
    this.heroSprite.setStrokeStyle(3, 0xffffff, 1);
    this.decoContainer.add(this.heroSprite);

    this.setupCamera();

    this.setupCameraDrag();

    this.setupTileClick();

    this.setupKeyboard();

    this.setupTooltip();

    this.floaters = new FloatingRewardManager(this, this.mapContainer, () => ({
      x: this.heroSprite.x,
      y: this.heroSprite.y,
    }));
    this.unsubFloating = this.app.onFloatingReward((r) => this.handleFloatingReward(r));

    this.unsub = this.app.onWorldState((s) => this.handleWorldState(s));
    const s0 = this.app.getWorldState();
    if (s0) {
      this.time.delayedCall(0, () => {
        if (!this.scene.isActive()) return;
        const s = this.app.getWorldState();
        if (s) this.handleWorldState(s);
      });
    }

    this.game.events.on("minimap:navigate", this.onMinimapNavigate, this);
    this.game.events.on("hud:centerHero", this.centerOnHero, this);

    this.resizeHandler = () => this.onResize();
    this.scale.on("resize", this.resizeHandler);

    this.events.on(Phaser.Scenes.Events.PAUSE, this.onPause, this);
    this.events.on(Phaser.Scenes.Events.RESUME, this.onResume, this);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.shutdown());
  }

  shutdown() {
    this.stopMoveAnimation();
    this.unsub?.();
    this.unsubFloating?.();
    this.floaters?.destroyAll();
    this.game.events.off("minimap:navigate", this.onMinimapNavigate, this);
    this.game.events.off("hud:centerHero", this.centerOnHero, this);
    if (this.resizeHandler) {
      this.scale.off("resize", this.resizeHandler);
      this.resizeHandler = undefined;
    }
    this.events.off(Phaser.Scenes.Events.PAUSE, this.onPause, this);
    this.events.off(Phaser.Scenes.Events.RESUME, this.onResume, this);
    this.terrainPool = [];
    this.terrainPoolIdx = 0;
    this.decoPool = [];
    this.decoPoolIdx = 0;
  }

  private setupCamera() {
    const state = this.app.getWorldState();
    const width = state?.map.width ?? DEFAULT_MAP_SIZE;
    const height = state?.map.height ?? DEFAULT_MAP_SIZE;

    const worldW = width * TILE_SIZE;
    const worldH = height * TILE_SIZE;

    const cam = this.cameras.main;
    cam.setBounds(0, 0, worldW, worldH);

    this.centerOnHero();
  }

  private centerOnHero() {
    const state = this.app.getWorldState();
    if (!state) return;

    const heroPx = state.hero.x * TILE_SIZE + TILE_SIZE / 2;
    const heroPy = state.hero.y * TILE_SIZE + TILE_SIZE / 2;

    this.cameras.main.centerOn(heroPx, heroPy);
  }

  private handleFloatingReward = (r: import("../app/GameApp").FloatingRewardEvent) => {
    if (r.tileX != null && r.tileY != null && this.isAnimatingMove) {
      this.pendingFloaters.push(r);
      return;
    }
    if (r.tileX != null && r.tileY != null) {
      const x = r.tileX * TILE_SIZE + TILE_SIZE / 2;
      const y = r.tileY * TILE_SIZE + TILE_SIZE / 2;
      this.floaters?.spawn(r, { x, y });
      return;
    }
    this.floaters?.spawn(r);
  };

  private flushFloatersForStep(stepX: number, stepY: number) {
    const remaining: typeof this.pendingFloaters = [];
    for (const f of this.pendingFloaters) {
      if (f.tileX === stepX && f.tileY === stepY) {
        const px = stepX * TILE_SIZE + TILE_SIZE / 2;
        const py = stepY * TILE_SIZE + TILE_SIZE / 2;
        this.floaters?.spawn(f, { x: px, y: py });
      } else {
        remaining.push(f);
      }
    }
    this.pendingFloaters = remaining;
  }

  private flushRemainingFloaters() {
    for (const f of this.pendingFloaters) {
      if (f.tileX != null && f.tileY != null) {
        const px = f.tileX * TILE_SIZE + TILE_SIZE / 2;
        const py = f.tileY * TILE_SIZE + TILE_SIZE / 2;
        this.floaters?.spawn(f, { x: px, y: py });
      } else {
        this.floaters?.spawn(f);
      }
    }
    this.pendingFloaters = [];
  }

  private onPause = () => {
    this.stopMoveAnimation();
    this.input.enabled = false;
    this.input.keyboard?.resetKeys();
    this.pointerdownInside = false;
    this.isDragging = false;
    this.tooltip?.setVisible(false);
  };

  private onResume = () => {
    this.input.enabled = true;
    this.maybeRenderContinuation();
  };

  private onMinimapNavigate(data: { tileX: number; tileY: number }) {
    const px = data.tileX * TILE_SIZE + TILE_SIZE / 2;
    const py = data.tileY * TILE_SIZE + TILE_SIZE / 2;
    this.cameras.main.centerOn(px, py);
  }

  private onResize() {
    this.setupCamera();
  }

  private setupCameraDrag() {
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      this.pointerdownInside = true;
      this.isDragging = false;
      this.dragStartX = pointer.x;
      this.dragStartY = pointer.y;
      this.camStartX = this.cameras.main.scrollX;
      this.camStartY = this.cameras.main.scrollY;
    });

    this.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (!this.pointerdownInside || !pointer.isDown) return;

      const dx = pointer.x - this.dragStartX;
      const dy = pointer.y - this.dragStartY;

      if (!this.isDragging) {
        if (Math.abs(dx) < this.DRAG_THRESHOLD && Math.abs(dy) < this.DRAG_THRESHOLD) return;
        this.isDragging = true;
        this.tooltip?.setVisible(false);
      }

      this.cameras.main.scrollX = this.camStartX - dx;
      this.cameras.main.scrollY = this.camStartY - dy;
    });

    this.input.on("pointerupoutside", () => {
      this.pointerdownInside = false;
      this.isDragging = false;
    });
  }

  private setupTileClick() {
    this.input.on("pointerup", (pointer: Phaser.Input.Pointer) => {
      const wasInside = this.pointerdownInside;
      const wasDrag = this.isDragging;
      this.pointerdownInside = false;
      this.isDragging = false;
      if (!wasInside || wasDrag) return;

      const dx = Math.abs(pointer.x - this.dragStartX);
      const dy = Math.abs(pointer.y - this.dragStartY);
      if (dx >= this.DRAG_THRESHOLD || dy >= this.DRAG_THRESHOLD) return;

      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const tileX = Math.floor(worldPoint.x / TILE_SIZE);
      const tileY = Math.floor(worldPoint.y / TILE_SIZE);

      this.onTileClick(tileX, tileY);
    });
  }

  private setupTooltip() {
    this.tooltip = this.add.container(0, 0).setDepth(1000).setVisible(false);

    this.input.on("pointermove", (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        this.tooltip?.setVisible(false);
        return;
      }
      const worldPoint = this.cameras.main.getWorldPoint(pointer.x, pointer.y);
      const tileX = Math.floor(worldPoint.x / TILE_SIZE);
      const tileY = Math.floor(worldPoint.y / TILE_SIZE);
      this.updateTooltip(tileX, tileY, pointer.x, pointer.y);
    });
  }

  private renderTooltipPanel(opts: { title: string; rows: Array<{ label: string; value: string }>; desc?: string }): { width: number; height: number } {
    if (!this.tooltip) return { width: 0, height: 0 };
    this.tooltip.removeAll(true);

    const padX = 14, padY = 12;
    const innerW = 230;
    const rowH = 18;
    const titleH = 22;

    const titleText = this.add.text(padX, padY, opts.title.toUpperCase(), {
      fontFamily: "Cinzel, serif", fontSize: "15px", color: "#c8a04a", fontStyle: "bold",
      stroke: "#000", strokeThickness: 3,
    });
    titleText.setLetterSpacing(2);

    const elements: Phaser.GameObjects.Text[] = [titleText];
    let y = padY + titleH + 6;

    for (const row of opts.rows) {
      const left = this.add.text(padX, y, row.label, {
        fontFamily: "Cinzel, serif", fontSize: "12px", color: "#8a9a85",
      });
      const right = this.add.text(padX + innerW, y, row.value, {
        fontFamily: "Cinzel, serif", fontSize: "13px", color: "#f4ecd6", fontStyle: "bold",
        stroke: "#000", strokeThickness: 2,
      });
      right.setOrigin(1, 0);
      elements.push(left, right);
      y += rowH;
    }

    let descText: Phaser.GameObjects.Text | null = null;
    if (opts.desc) {
      y += 4;
      descText = this.add.text(padX, y, opts.desc, {
        fontFamily: "'Segoe UI', sans-serif", fontSize: "11px",
        color: "#b8b0a0", fontStyle: "italic",
        wordWrap: { width: innerW + 14 },
      });
      elements.push(descText);
      y += descText.height;
    }

    const boxW = innerW + padX * 2 + 14;
    const boxH = y + padY;

    const bg = this.add.graphics();
    bg.fillStyle(0x1c1a26, 0.97);
    bg.fillRoundedRect(0, 0, boxW, boxH, 8);
    bg.lineStyle(2, 0xc8a04a, 0.9);
    bg.strokeRoundedRect(0, 0, boxW, boxH, 8);
    bg.lineStyle(1, 0x3a3548, 0.6);
    bg.lineBetween(padX, padY + titleH + 2, boxW - padX, padY + titleH + 2);
    if (opts.desc) {
      const sepY = (descText!.y) - 4;
      bg.lineBetween(padX, sepY, boxW - padX, sepY);
    }

    this.tooltip.add(bg);
    this.tooltip.add(elements);

    return { width: boxW, height: boxH };
  }

  private updateTooltip(tileX: number, tileY: number, screenX: number, screenY: number) {
    const state = this.app.getWorldState();
    if (!state || !this.tooltip) {
      this.tooltip?.setVisible(false);
      return;
    }

    const { width, height, tiles } = state.map;
    if (tileX < 0 || tileX >= width || tileY < 0 || tileY >= height) {
      this.tooltip.setVisible(false);
      return;
    }

    const tile = tiles[tileY][tileX];
    if (!tile.fog.discovered) {
      this.tooltip.setVisible(false);
      return;
    }

    let panel: { title: string; rows: Array<{ label: string; value: string }>; desc?: string } | null = null;

    if (tile.content.kind === "event") {
      const ids = (tile.content as { eventIds?: string[]; eventId?: string }).eventIds
        ?? [(tile.content as { eventId: string }).eventId];
      const ev = ids.map(id => state.events.byId[id]).find(e => e && !e.triggered);
      if (ev) {
        if (ev.payload.kind === "battle") {
          const tpl = ENEMY_TEMPLATES[ev.payload.enemyTemplateId];
          if (tpl) {
            const lvl = ev.payload.enemyLevel ?? tpl.level;
            const scaled = scaleEnemyToLevel(tpl, lvl);
            const hp = ev.payload.enemyMaxHp ?? scaled.maxHp;
            panel = {
              title: tpl.name,
              rows: [
                { label: "Уровень", value: String(lvl) },
                { label: "HP", value: String(hp) },
                { label: "Броня", value: String(scaled.armor) },
                { label: "Уклонение", value: String(scaled.evasion) },
              ],
              desc: ev.payload.text || undefined,
            };
          } else {
            panel = { title: "Битва", rows: [], desc: ev.payload.text };
          }
        } else if (ev.payload.kind === "vote") {
          panel = { title: "Голосование", rows: [], desc: ev.payload.question || ev.payload.text || "" };
        } else {
          panel = { title: "Событие", rows: [], desc: ev.payload.text };
        }
      }
    }

    if (tile.content.kind === "resource") {
      const res = state.resources.byId[tile.content.resourceId];
      if (res) {
        panel = {
          title: getResourcePileLabel(res.resourceType),
          rows: [{ label: "Количество", value: `+${res.amount}` }],
        };
      }
    }

    if (!panel) {
      this.tooltip.setVisible(false);
      return;
    }

    const { width: boxWidth, height: boxHeight } = this.renderTooltipPanel(panel);
    const tooltipX = Math.min(screenX + 15, this.scale.width - boxWidth - 10);
    const tooltipY = Math.min(screenY + 15, this.scale.height - boxHeight - 10);
    this.tooltip.setPosition(tooltipX, tooltipY);
    this.tooltip.setVisible(true);
    this.tooltip.setScrollFactor(0);
  }

  private setupKeyboard() {
    this.input.keyboard?.on("keydown-SPACE", () => this.executePlannedMove());
    this.input.keyboard?.on("keydown-ESC", () => this.clearPlannedPath());
  }

  private onTileClick(tileX: number, tileY: number) {
    if (this.isAnimatingMove || this.app.isMovementLocked()) return;
    const state = this.app.getWorldState();
    if (!state) return;

    const { width, height, tiles } = state.map;
    if (tileX < 0 || tileX >= width || tileY < 0 || tileY >= height) return;

    const tile = tiles[tileY][tileX];
    if (!tile.fog.discovered) return;
    if (!tile.terrain.passable) {
      this.clearPlannedPath();
      return;
    }

    const heroX = state.hero.x;
    const heroY = state.hero.y;
    if (tileX === heroX && tileY === heroY) {
      this.clearPlannedPath();
      return;
    }

    if (this.pendingTarget && this.pendingTarget.x === tileX && this.pendingTarget.y === tileY) {
      this.executePlannedMove();
      return;
    }

    const path = findPath(state, heroX, heroY, tileX, tileY, Number.POSITIVE_INFINITY);
    if (!path || path.length === 0) {
      this.clearPlannedPath();
      return;
    }

    this.originalMoveTarget = undefined;
    this.pendingTarget = { x: tileX, y: tileY };
    this.plannedPath = path;
    this.renderState(state);
  }

  private clearPlannedPath() {
    if (!this.pendingTarget && !this.plannedPath) return;
    this.pendingTarget = undefined;
    this.plannedPath = undefined;
    this.originalMoveTarget = undefined;
    const s = this.app.getWorldState();
    if (s) this.renderState(s);
  }

  private executePlannedMove() {
    if (!this.pendingTarget || this.isAnimatingMove || this.app.isMovementLocked()) return;
    const target = this.pendingTarget;
    this.originalMoveTarget = { ...target };
    this.pendingTarget = undefined;
    this.plannedPath = undefined;
    this.app.move(target.x, target.y);
  }

  private maybeRenderContinuation() {
    if (!this.originalMoveTarget) return;
    const state = this.app.getWorldState();
    if (!state) return;
    if (this.isAnimatingMove || this.app.isMovementLocked()) return;
    if (this.app.getCurrentMode() !== "map") return;
    const hero = state.hero;
    if (hero.x === this.originalMoveTarget.x && hero.y === this.originalMoveTarget.y) {
      this.originalMoveTarget = undefined;
      return;
    }
    const path = findPath(state, hero.x, hero.y, this.originalMoveTarget.x, this.originalMoveTarget.y, Number.POSITIVE_INFINITY);
    if (!path || path.length === 0) {
      this.originalMoveTarget = undefined;
      return;
    }
    this.pendingTarget = { ...this.originalMoveTarget };
    this.plannedPath = path;
    this.renderState(state);
  }

  private renderState(state: WorldState) {
    const { width, height, tiles } = state.map;

    const worldW = width * TILE_SIZE;
    const worldH = height * TILE_SIZE;
    this.cameras.main.setBounds(0, 0, worldW, worldH);

    this.mapGraphics.clear();
    this.pathGraphics.clear();
    this.terrainPoolIdx = 0;
    this.decoPoolIdx = 0;

    const cam = this.cameras.main;
    const startTileX = Math.max(0, Math.floor(cam.scrollX / TILE_SIZE) - 1);
    const startTileY = Math.max(0, Math.floor(cam.scrollY / TILE_SIZE) - 1);
    const endTileX = Math.min(width, Math.ceil((cam.scrollX + cam.width) / TILE_SIZE) + 1);
    const endTileY = Math.min(height, Math.ceil((cam.scrollY + cam.height) / TILE_SIZE) + 1);

    for (let y = startTileY; y < endTileY; y++) {
      for (let x = startTileX; x < endTileX; x++) {
        const tile = tiles[y][x];
        const px = x * TILE_SIZE;
        const py = y * TILE_SIZE;

        this.renderTerrain(tile, px, py, x, y, state);

        if (tile.fog.discovered) {
          this.renderTileContent(tile, px, py, state);
        }

      }
    }

    for (let i = this.terrainPoolIdx; i < this.terrainPool.length; i++) {
      this.terrainPool[i].setVisible(false);
    }
    for (let i = this.decoPoolIdx; i < this.decoPool.length; i++) {
      this.decoPool[i].setVisible(false);
    }

    this.heroSprite.setDepth(this.heroSprite.y);
    this.decoContainer.sort("depth");

    if (!this.isAnimatingMove && this.plannedPath && this.plannedPath.length > 0) {
      this.renderPlannedPath();
    }

    if (!this.isAnimatingMove) {
      const heroPx = state.hero.x * TILE_SIZE + TILE_SIZE / 2;
      const heroPy = state.hero.y * TILE_SIZE + TILE_SIZE / 2;
      this.heroSprite.setPosition(heroPx, heroPy);
    }
  }

  private renderPlannedPath() {
    if (!this.plannedPath) return;
    const path = this.plannedPath;
    const last = path.length - 1;

    for (let i = 0; i < last; i++) {
      const step = path[i];
      const cx = step.x * TILE_SIZE + TILE_SIZE / 2;
      const cy = step.y * TILE_SIZE + TILE_SIZE / 2;
      this.pathGraphics.fillStyle(0x0c0a14, 0.55);
      this.pathGraphics.fillCircle(cx, cy, 9);
      this.pathGraphics.fillStyle(0xfbbf24, 0.95);
      this.pathGraphics.fillCircle(cx, cy, 7);
    }

    const end = path[last];
    const ex = end.x * TILE_SIZE + TILE_SIZE / 2;
    const ey = end.y * TILE_SIZE + TILE_SIZE / 2;
    const inset = 6;
    const size = TILE_SIZE - inset * 2;
    this.pathGraphics.fillStyle(0xfbbf24, 0.2);
    this.pathGraphics.fillRect(ex - size / 2, ey - size / 2, size, size);
    this.pathGraphics.lineStyle(3, 0xfbbf24, 1);
    this.pathGraphics.strokeRect(ex - size / 2, ey - size / 2, size, size);
    this.pathGraphics.lineStyle(2, 0x0c0a14, 0.7);
    this.pathGraphics.strokeRect(ex - size / 2 - 1, ey - size / 2 - 1, size + 2, size + 2);
  }

  private handleWorldState(state: WorldState) {
    if (!this.scene.isActive()) return;
    const nextHero = { x: state.hero.x, y: state.hero.y };
    if (!this.lastHeroPos) {
      this.lastHeroPos = { ...nextHero };
      this.renderState(state);
      this.cameras.main.centerOn(
        nextHero.x * TILE_SIZE + TILE_SIZE / 2,
        nextHero.y * TILE_SIZE + TILE_SIZE / 2,
      );

      if (state.turn === 0) {
        this.app.openCutscene([
          { text: "Дорогие круговчане!" },
          { text: "Добро пожаловать в рощу!" },
        ]);
      }
      return;
    }

    if (this.lastHeroPos.x === nextHero.x && this.lastHeroPos.y === nextHero.y) {
      this.renderState(state);
      return;
    }

    this.startMoveAnimation(state, this.lastHeroPos, nextHero);
  }

  private stopMoveAnimation() {
    window.clearTimeout(this.safetyTimeoutId);
    this.safetyTimeoutId = undefined;
    if (this.movementTween) {
      this.movementTween.stop();
      this.movementTween = undefined;
    }
    if (this.isAnimatingMove) {
      this.isAnimatingMove = false;
      this.app.setMovementLocked(false);
    }
    this.flushRemainingFloaters();
  }

  private startMoveAnimation(state: WorldState, from: { x: number; y: number }, to: { x: number; y: number }) {
    this.stopMoveAnimation();
    this.pendingTarget = undefined;
    this.plannedPath = undefined;

    const maxDist = state.map.width * state.map.height;
    const path = findPath(state, from.x, from.y, to.x, to.y, maxDist);
    if (!path || path.length === 0) {
      this.lastHeroPos = { ...to };
      this.renderState(state);
      return;
    }

    this.isAnimatingMove = true;
    this.app.setMovementLocked(true);
    this.renderState(state);

    const startPx = from.x * TILE_SIZE + TILE_SIZE / 2;
    const startPy = from.y * TILE_SIZE + TILE_SIZE / 2;
    this.heroSprite.setPosition(startPx, startPy);

    const finishAnimation = () => {
      if (!this.scene.isActive()) return;
      this.isAnimatingMove = false;
      this.app.setMovementLocked(false);
      this.lastHeroPos = { ...to };
      this.heroSprite.setPosition(to.x * TILE_SIZE + TILE_SIZE / 2, to.y * TILE_SIZE + TILE_SIZE / 2);
      this.renderState(this.app.getWorldState() ?? state);
      this.maybeRenderContinuation();
    };

    this.safetyTimeoutId = window.setTimeout(() => {
      if (this.isAnimatingMove) {
        finishAnimation();
      }
    }, path.length * 200 + 500);

    const tweenChain = this.tweens.chain({
      targets: this.heroSprite,
      tweens: path.map((step) => ({
        x: step.x * TILE_SIZE + TILE_SIZE / 2,
        y: step.y * TILE_SIZE + TILE_SIZE / 2,
        duration: 140,
        ease: "Sine.easeInOut",
        onUpdate: () => {
          this.heroSprite.setDepth(this.heroSprite.y);
          this.decoContainer.sort("depth");
        },
        onComplete: () => this.flushFloatersForStep(step.x, step.y),
      })),
      onComplete: () => {
        window.clearTimeout(this.safetyTimeoutId);
        this.flushRemainingFloaters();
        finishAnimation();
      },
    });

    this.movementTween = tweenChain;

    if ("play" in tweenChain && typeof tweenChain.play === "function") {
      tweenChain.play();
    }
  }

  private renderTerrain(tile: TileState, px: number, py: number, tileX: number, tileY: number, state: WorldState) {
    const terrain = tile.terrain;
    const fog = tile.fog;

    if (!fog.discovered) {
      this.mapGraphics.fillStyle(FOG_COLORS.undiscovered, 1);
      this.mapGraphics.fillRect(px, py, TILE_SIZE, TILE_SIZE);
      return;
    }

    const alpha = 1;

    const terrainKey = this.selectTerrainKey(tile, tileX, tileY, state);
    if (this.textures.exists(terrainKey)) {
      const sprite = this.getTerrainSprite(terrainKey);
      sprite.setPosition(px + TILE_SIZE / 2, py + TILE_SIZE / 2);
      sprite.setDisplaySize(TILE_SIZE, TILE_SIZE);
      sprite.setAlpha(alpha);
      sprite.setVisible(true);
    } else {
      const baseColor = TERRAIN_COLORS[terrain.type] ?? TERRAIN_COLORS.grass;
      this.mapGraphics.fillStyle(baseColor, alpha);
      this.mapGraphics.fillRect(px, py, TILE_SIZE, TILE_SIZE);
    }

    this.mapGraphics.lineStyle(1, 0x000000, 0.15);
    this.mapGraphics.strokeRect(px, py, TILE_SIZE, TILE_SIZE);

    if (terrain.decoration) {
      this.renderDecoration(terrain.decoration, px, py, alpha, tileX, tileY);
    }

    if (!terrain.passable) {
      this.mapGraphics.lineStyle(2, 0xef4444, 0.3);
      this.mapGraphics.lineBetween(px + 4, py + 4, px + TILE_SIZE - 4, py + TILE_SIZE - 4);
      this.mapGraphics.lineBetween(px + TILE_SIZE - 4, py + 4, px + 4, py + TILE_SIZE - 4);
    }
  }

  private getTerrainSprite(textureKey: string): Phaser.GameObjects.Image {
    while (this.terrainPoolIdx < this.terrainPool.length) {
      const img = this.terrainPool[this.terrainPoolIdx];
      if (!img.scene) {
        this.terrainPool.splice(this.terrainPoolIdx, 1);
        continue;
      }
      this.terrainPoolIdx++;
      img.setTexture(textureKey);
      return img;
    }
    const img = this.add.image(0, 0, textureKey).setVisible(false);
    this.terrainContainer.add(img);
    this.terrainPool.push(img);
    this.terrainPoolIdx++;
    return img;
  }

  
  private selectTerrainKey(tile: TileState, tileX: number, tileY: number, state: WorldState): string {
    const rawType = tile.terrain.type;
    const type = rawType === 'forest' ? 'grass' : rawType;
    const isForest = rawType === 'forest';
    const { tiles, width, height } = state.map;

    const BASE_VARIANTS: Partial<Record<string, number>> = { grass: 3, sand: 1, water: 1, road: 1 };
    const variantCount = BASE_VARIANTS[type] ?? 1;
    const variant = (tileHash(tileX, tileY, 99) % variantCount) + 1;
    const baseKey = `terrain-${type}-${variant}`;

    const TRANSITIONS: [string, string][] = [
      ['grass', 'sand'],
      ['grass', 'road'],
      ['sand', 'water'],
    ];

    const neighbor = (dx: number, dy: number): string | null => {
      const nx = tileX + dx;
      const ny = tileY + dy;
      if (nx < 0 || nx >= width || ny < 0 || ny >= height) return null;
      const t = tiles[ny][nx].terrain.type;
      return t === 'forest' ? 'grass' : t;
    };

    for (const [a, b] of TRANSITIONS) {
      if (type !== a) continue;
      if (isForest && b === 'road') continue;

      const n = neighbor( 0, -1) === b;
      const s = neighbor( 0,  1) === b;
      const e = neighbor( 1,  0) === b;
      const w = neighbor(-1,  0) === b;

      if (n && w) return `terrain-${a}-${b}-09`; 
      if (n && e) return `terrain-${a}-${b}-07`; 
      if (s && w) return `terrain-${a}-${b}-03`; 
      if (s && e) return `terrain-${a}-${b}-01`; 

      if (n) return `terrain-${a}-${b}-08`;
      if (s) return `terrain-${a}-${b}-02`;
      if (e) return `terrain-${a}-${b}-04`;
      if (w) return `terrain-${a}-${b}-06`;

    }

    return baseKey;
  }

  
  private getDecoSprite(textureKey: string): Phaser.GameObjects.Image {
    while (this.decoPoolIdx < this.decoPool.length) {
      const img = this.decoPool[this.decoPoolIdx];
      if (!img.scene) {
        this.decoPool.splice(this.decoPoolIdx, 1);
        continue;
      }
      this.decoPoolIdx++;
      img.setTexture(textureKey);
      return img;
    }
    const img = this.add.image(0, 0, textureKey).setVisible(false);
    this.decoContainer.add(img);
    this.decoPool.push(img);
    this.decoPoolIdx++;
    return img;
  }

  private renderDecoration(deco: string, px: number, py: number, alpha: number, tileX: number, tileY: number) {
    if (deco === "wave") {
      const cy = py + TILE_SIZE / 2;
      this.mapGraphics.lineStyle(2, 0x38bdf8, alpha * 0.6);
      this.mapGraphics.beginPath();
      this.mapGraphics.moveTo(px + 8, cy);
      this.mapGraphics.lineTo(px + 16, cy - 6);
      this.mapGraphics.lineTo(px + 24, cy);
      this.mapGraphics.lineTo(px + 32, cy - 6);
      this.mapGraphics.lineTo(px + 40, cy);
      this.mapGraphics.lineTo(px + 48, cy - 6);
      this.mapGraphics.lineTo(px + 56, cy);
      this.mapGraphics.strokePath();
      return;
    }

    const cfg = DECO_CONFIG[deco];
    if (!cfg) return;
    const variantCount = DECO_VARIANTS[deco] ?? 1;

    const h0 = tileHash(tileX, tileY, 0);
    const count = cfg.count[0] + (h0 % (cfg.count[1] - cfg.count[0] + 1));

    const tileVariant = (h0 % variantCount) + 1;

    for (let i = 0; i < count; i++) {
      const h = tileHash(tileX, tileY, i + 1);

      const variant = cfg.sameVariant ? tileVariant : (h % variantCount) + 1;
      const textureKey = `${deco}_0${variant}`;
      if (!this.textures.exists(textureKey)) continue;

      const ox = ((h >> 8) % (cfg.offsetRange * 2 + 1)) - cfg.offsetRange;
      const oy = ((h >> 16) % (cfg.offsetRange * 2 + 1)) - cfg.offsetRange;

      const scaleFrac = ((h >> 4) % 100) / 100;
      const scale = cfg.scale[0] + scaleFrac * (cfg.scale[1] - cfg.scale[0]);
      const size = TILE_SIZE * scale;

      const sprite = this.getDecoSprite(textureKey);
      sprite.setPosition(px + TILE_SIZE / 2 + ox, py + TILE_SIZE / 2 + oy);
      sprite.setDisplaySize(size, size);
      sprite.setAlpha(alpha);
      sprite.setVisible(true);
      sprite.setDepth(py + TILE_SIZE / 2 + oy);
    }
  }

  private renderTileContent(tile: TileState, px: number, py: number, state: WorldState) {
    const alpha = 1;
    const cx = px + TILE_SIZE / 2;
    const cy = py + TILE_SIZE / 2;

    if (tile.content.kind === "event") {
      const ids = (tile.content as { eventIds?: string[]; eventId?: string }).eventIds
        ?? [(tile.content as { eventId: string }).eventId];
      const ev = ids.map(id => state.events.byId[id]).find(e => e && !e.triggered);
      if (ev) {
        if (ev.payload.kind === "battle") {
          this.mapGraphics.fillStyle(0x8b5cf6, alpha * 0.3);
          this.mapGraphics.fillCircle(cx, cy, 20);
          this.mapGraphics.fillStyle(0x8b5cf6, alpha);
          this.mapGraphics.fillCircle(cx, cy, 14);

          this.mapGraphics.lineStyle(3, 0xffffff, alpha);
          this.mapGraphics.lineBetween(cx - 8, cy - 8, cx + 8, cy + 8);
          this.mapGraphics.lineBetween(cx + 8, cy - 8, cx - 8, cy + 8);

          this.mapGraphics.fillStyle(0xffffff, alpha);
          this.mapGraphics.fillCircle(cx - 8, cy - 8, 3);
          this.mapGraphics.fillCircle(cx + 8, cy - 8, 3);
        } else if (ev.payload.kind === "vote") {
          this.mapGraphics.fillStyle(0x3b82f6, alpha * 0.3);
          this.mapGraphics.fillCircle(cx, cy, 20);
          this.mapGraphics.fillStyle(0x3b82f6, alpha);
          this.mapGraphics.fillCircle(cx, cy, 14);

          if (!this.children.getByName(`vote-${tile.x}-${tile.y}`)) {
            const txt = this.add.text(cx, cy, "?", {
              fontSize: "18px", fontFamily: "monospace", color: "#ffffff",
            }).setOrigin(0.5).setAlpha(alpha).setName(`vote-${tile.x}-${tile.y}`);
            txt.setDepth(10);
          }
        } else {
          this.mapGraphics.fillStyle(0xef4444, alpha * 0.3);
          this.mapGraphics.fillCircle(cx, cy, 18);
          this.mapGraphics.fillStyle(0xef4444, alpha);
          this.mapGraphics.fillCircle(cx, cy, 12);
          this.mapGraphics.fillStyle(0xffffff, alpha);
          this.mapGraphics.fillCircle(cx - 3, cy - 3, 4);
        }
      }
    }

    if (tile.content.kind === "resource" && tile.content.resourceId) {
      const res = state.resources.byId[tile.content.resourceId];
      if (res) {
        const color = RESOURCE_COLORS[res.resourceType];
        this.mapGraphics.fillStyle(color, alpha * 0.4);
        this.mapGraphics.fillCircle(cx, cy, 16);
        this.mapGraphics.fillStyle(color, alpha);
        this.mapGraphics.fillCircle(cx, cy, 10);
      }
    }
  }

  private lastCamX = -1;
  private lastCamY = -1;

  update() {
    const cam = this.cameras.main;
    const state = this.app.getWorldState();
    const firstFrame = this.lastCamX < 0;
    const camMoved = cam.scrollX !== this.lastCamX || cam.scrollY !== this.lastCamY;
    if (state && (firstFrame || camMoved)) {
      this.lastCamX = cam.scrollX;
      this.lastCamY = cam.scrollY;
      this.renderState(state);
    }
  }
}
